import React from "react";

const { useEffect } = React;

type FocusTrapParams = {
  containerRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  isOpen: boolean;
  cancelFocus?: boolean;
};

export const useFocusTrap = ({
  isOpen,
  containerRef,
  onClose,
  cancelFocus,
}: FocusTrapParams): void => {
  useEffect(() => {
    if (!isOpen || !containerRef.current || cancelFocus) return;
    // Get the element that trigger the focus trap or that opens drawer or dialog
    const previouslyFocusedEl = document.activeElement as HTMLElement;
    // Make sure container is focusable
    if (!containerRef.current.hasAttribute("tabindex"))
      containerRef.current.setAttribute("tabindex", "-1");
    // Get a list of all focusable elements
    const focusableSelectors =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableEls = Array.from(
      containerRef.current.querySelectorAll(focusableSelectors),
    ).filter(
      (el): el is HTMLElement =>
        !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
    );

    const [firstEl] = focusableEls;
    const lastEl = focusableEls[focusableEls.length - 1];

    // Shift focus to first tababble el in drawer after elements have been painted due to CSS transition
    const handleTransitionEnd = () => {
      firstEl?.focus();
      // Remove listener after first call
      containerRef.current?.removeEventListener(
        "transitionend",
        handleTransitionEnd,
      );
    };

    containerRef.current?.addEventListener(
      "transitionend",
      handleTransitionEnd,
    );

    // Trap focus inside drawer
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Close drawer on Escape
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }

      if (e.key === "Tab" && document.activeElement === previouslyFocusedEl)
        firstEl.focus();

      // Trap and set focus to first and last elements within the drawer dialog
      if (e.key === "Tab" && focusableEls.length > 0) {
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl?.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Clean up event listener and set the focus back to the trigger element
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedEl?.focus();
    };
  }, [isOpen, containerRef, onClose, cancelFocus]);
};
